import { stringify } from "querystring";

import { ChangeEvent, useCallback, useEffect, useState } from "react";

import { debounce } from "@s-libs/micro-dash";
import { useRouter } from "next/router";

import { CondensedSearchButton } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu/CondensedSearchButton";
import { ExpandedSearchInput } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu/ExpandedSearchInput";
import { ISuggestion } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu/SearchMenu.constants";
import { fetchSuggestions } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu/SearchMenu.utils";
import { SearchSuggestionsPopout } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu/SearchSuggestionsPopout";
import { NavButtonWithPopout } from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/NavButtonWithPopout";
import { useSearchAdminPermissions } from "@/search/Langston/useSearchAdminPermissions";
import useUser from "@/users/useUser";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { useNewSearchSessionTracker } from "@/utils/snitch";
import { getSuggestionsForCurrentWeek } from "./defaultSuggestions";

export const SEARCH_MENU_ID = "nav-search-menu";

interface SearchMenuProps {
  onToggle: (value: boolean) => void;
  isAISearchEnabled: boolean;
}

const SearchMenu = ({ onToggle, isAISearchEnabled }: SearchMenuProps) => {
  const { push, query, pathname } = useRouter();
  const { data: user } = useUser();

  const searchParams = new URLSearchParams(stringify(query));
  const needleValue = searchParams.get(LangstonParameterSchema.needle.key);
  const [inputValue, setInputValue] = useState(needleValue || "");

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<ISuggestion[] | string[]>([]);
  const [previousSuggestions, setPreviousSuggestions] = useState<
    ISuggestion[] | string[]
  >([]);
  const [defaultSuggestions, setDefaultSuggestions] = useState<
    ISuggestion[] | string[]
  >([]);

  const { startSearchTracker } = useNewSearchSessionTracker();

  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  const [langstonSuggestionsExperiment] = useGetUserExperimentSegment(
    "langston_suggestions",
  );
  const isLangstonSuggestionsOn = langstonSuggestionsExperiment === "on";

  useEffect(() => {
    if (isLangstonSuggestionsOn) {
      const suggestions = getSuggestionsForCurrentWeek();
      setPreviousSuggestions(suggestions);
      setDefaultSuggestions(suggestions);
      return;
    }

    const fetchDefaultSuggestions = async () => {
      const suggestions = await fetchSuggestions({
        fragment: "",
        isLangstonSuggestionsOn: false,
      });
      setPreviousSuggestions(suggestions);
      setDefaultSuggestions(suggestions);
    };
    void fetchDefaultSuggestions();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(stringify(query));
    const needle = searchParams.get(LangstonParameterSchema.needle.key) || "";

    setInputValue(needle);
  }, [stringify(query)]);

  const handleToggle = () => {
    const newValue = !isAISearchEnabled;
    if (pathname === "/search") {
      // If we're on the search page,
      // reset query params to a default state.
      void push({
        pathname: "/search",
      });
    }
    onToggle(newValue);
  };

  const debouncedFetchSuggestions = useCallback(
    debounce(async (inputValue: string) => {
      if (!inputValue) {
        setSuggestions([]);
        return;
      }

      const suggestions = await fetchSuggestions({
        fragment: inputValue,
        isLangstonSuggestionsOn,
      });

      setSuggestions(suggestions);
      if (suggestions?.length) setPreviousSuggestions(suggestions);
    }, 500),
    [],
  );

  if (!user?.is_authenticated) return null;

  const resetSearchMenuToDefault = () => {
    setInputValue("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    const inputValue = event?.target?.value;
    setInputValue(inputValue);
    debouncedFetchSuggestions(inputValue);

    if (!inputValue) {
      resetSearchMenuToDefault();
    } else {
      setShowSuggestions(true);
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const [firstSuggestion] = document.querySelectorAll(
        ".search-suggestion-anchor",
      );
      (firstSuggestion as HTMLAnchorElement)?.focus();
    }
    if (event.key === "Escape") {
      setShowSuggestions(false);
      return;
    }
    if (event.key === "Enter") {
      setShowSuggestions(false);

      const searchParams = new URLSearchParams(stringify(query));
      searchParams.set(LangstonParameterSchema.needle.key, inputValue);

      if (inputValue) {
        // Initiate a search session in Sntich. A search session begins with a user searching for a needle.
        startSearchTracker(inputValue);
      }

      void push({
        pathname: "/search",
        search: `?${searchParams.toString()}`,
      });
      return;
    }
    setShowSuggestions(true);
  };

  const onFocus = () => {
    setShowSuggestions(true);
  };

  const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const isFocusStillInside = event.currentTarget.contains(
      event.relatedTarget as Node,
    );
    if (!isFocusStillInside) {
      setShowSuggestions(false);
    }
  };

  const getSuggestions = () => {
    if (suggestions?.length && inputValue) return suggestions;
    if (!suggestions?.length && !inputValue) return defaultSuggestions;
    return previousSuggestions;
  };

  return (
    <NavButtonWithPopout
      id="nav-search-suggestions"
      isOpen={showSuggestions}
      setIsOpen={setShowSuggestions}
      position={{ top: "4rem", left: 0, right: "-19.75rem" }}
      ButtonComponent={
        <div data-testid="nav-search-buttons">
          <CondensedSearchButton
            showSuggestions={showSuggestions}
            setShowSuggestions={setShowSuggestions}
          />
          <ExpandedSearchInput
            inputValue={inputValue}
            onFocus={onFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            showSuggestions={showSuggestions}
          />
        </div>
      }
      PopoutComponent={
        <SearchSuggestionsPopout
          onChange={onChange}
          onKeyDown={onKeyDown}
          inputValue={inputValue}
          setShowSuggestions={setShowSuggestions}
          suggestions={getSuggestions()}
          resetSearchMenuToDefault={resetSearchMenuToDefault}
          pressed={isAISearchEnabled}
          onToggle={handleToggle}
          onBlur={onBlur}
        />
      }
    />
  );
};

SearchMenu.displayName = "SearchInput";
export { SearchMenu };
